/**
 * Mock activity data for specific PhET simulations (LESS-5285).
 *
 * Only the 5 simulations listed below should display mock activities.
 * All other PhET simulations should not show any activities.
 *
 * This is a temporary solution until activities are pulled from Langston.
 */

const PHET_TEACHER_TIPS_BY_CONTENT_ID: Record<string, string> = {
  // Circuit Construction Kit: DC
  cmgl7sdlq001wl990mz1apzek:
    "https://prod-newsela-media.s3.us-east-1.amazonaws.com/phet-simulation/cmgl7sdlq001wl990mz1apzek/pdfs/teacher-tips.pdf",
  // Membrane Transport
  cmgl7sdlq0000l990c07ndm03:
    "https://prod-newsela-media.s3.us-east-1.amazonaws.com/phet-simulation/cmgl7sdlq0000l990c07ndm03/pdfs/teacher-tips.pdf",
  // Mean: Share and Balance
  cmgl7sdlq0007l990xcq1o2hw:
    "https://prod-newsela-media.s3.us-east-1.amazonaws.com/phet-simulation/cmgl7sdlq0007l990xcq1o2hw/pdfs/teacher-tips.pdf",
  // States of Matter: Basics
  cmgl7sdlq0026l990rwl9u95j:
    "https://prod-newsela-media.s3.us-east-1.amazonaws.com/phet-simulation/cmgl7sdlq0026l990rwl9u95j/pdfs/teacher-tips.pdf",
  // Gravity and Orbits
  cmgl7sdlq0028l990n888yr3w:
    "https://prod-newsela-media.s3.us-east-1.amazonaws.com/phet-simulation/cmgl7sdlq0028l990n888yr3w/pdfs/teacher-tips.pdf",
};

/**
 * Returns the Teacher Tips PDF URL for a whitelisted PhET simulation,
 * or null if the simulation is not in the whitelist.
 */
export function getPhetTeacherTipsPdfUrl(contentId: string): string | null {
  return PHET_TEACHER_TIPS_BY_CONTENT_ID[contentId] ?? null;
}

/**
 * Mock discussion questions per PhET simulation.
 *
 * Each entry maps a content ID to a JSON-stringified array of
 * IGeniusDiscussionQuestion objects (without a `section` field,
 * so they render as general / unsectioned questions).
 *
 * This is a temporary solution until discussion questions are pulled
 * from Langston.
 */
const PHET_DISCUSSION_QUESTIONS_BY_CONTENT_ID: Record<string, string> = {
  // Circuit Construction Kit: DC
  cmgl7sdlq001wl990mz1apzek: JSON.stringify([
    {
      question:
        "The Basics: Connect a battery, two wires, and a light bulb in a single loop. What must happen for the light bulb to turn on?",
      answer:
        'Students should see that a continuous, "closed" loop of conductors is required. They should notice that if any part of the loop is broken (an "open" circuit), the electrons stop moving and the bulb goes dark.',
    },
    {
      question:
        'Flow Direction: Toggle between "Electrons" and "Conventional" current in the right-hand menu. What is the difference between how the two are represented?',
      answer:
        'Students should observe that electrons flow from the negative to the positive terminal of the battery. They should notice that "Conventional" current is represented as flowing in the opposite direction (positive to negative).',
    },
    {
      question:
        'Conductivity: Drag the "Paper Clip" and the "Eraser" into your circuit one at a time between the wire and the light bulb. Why does the bulb light up with one but not the other?',
      answer:
        "Students should notice the paper clip allows current to flow (conductor), while the eraser stops it (insulator). They should describe how the blue spheres (electrons) stop moving entirely when the insulator is added.",
    },
    {
      question:
        "Voltage Effects: Click on the battery and use the slider to increase the voltage. How does this change the movement of the electrons and the appearance of the bulb?",
      answer:
        'Students should see the electrons move significantly faster as voltage increases. They should also notice the "rays" around the light bulb grow longer and brighter, indicating more energy is being converted.',
    },
    {
      question:
        'Resistance: Replace a wire with a "Resistor" and change its Ohms using the slider. What is the relationship between the amount of resistance and the speed of the current?',
      answer:
        'Students should observe that as resistance increases, the electron flow slows down. They should conclude that resistance "resists" the flow of charge, acting like a narrow pipe for water.',
    },
    {
      question:
        "Series Circuits: Build a circuit with two bulbs in a single loop (one after the other). How does the brightness of these bulbs compare to a circuit with only one bulb?",
      answer:
        "Students should notice that both bulbs are dimmer than a single bulb. They should see that the electrons move more slowly because the total resistance of the path has increased.",
    },
    {
      question:
        'Parallel Circuits: Build a circuit where the wires "split" so each bulb has its own path to the battery. What happens to the other bulb if you break the connection for just one of them?',
      answer:
        "Students should see that the other bulb stays lit. They should notice that in a parallel circuit, there are multiple independent paths for the current to follow, unlike the single-path series circuit.",
    },
    {
      question:
        'Measuring Current: Drag the "Ammeter" (the crosshair tool) over different parts of a parallel circuit. Is the current the same everywhere, or does it change?',
      answer:
        'Students should notice the current is highest near the battery and "splits" at the junctions. They should see that the sum of the currents in the individual branches equals the total current leaving the battery.',
    },
    {
      question:
        'Voltage Drops: Use the "Voltmeter" to measure the red and black ends across a bulb and then across a plain wire. What is the difference in the readings?',
      answer:
        'Students should see a voltage reading (a "drop") across the bulb but a reading of 0V across a plain wire. They should realize that voltage is "used" or dropped as energy is converted by the bulb.',
    },
    {
      question:
        'Short Circuits: Create a path from one end of the battery to the other using only wires (no bulbs or resistors). What happens to the simulation, and what does this tell you about "short circuits"?',
      answer:
        "Students should see the battery and wires catch on fire (graphically). They should notice the electrons move at an extremely high speed, indicating that without resistance, the current becomes dangerously high.",
    },
  ]),

  // Membrane Transport
  cmgl7sdlq0000l990c07ndm03: JSON.stringify([
    {
      question:
        "Simple Diffusion: Start with a high concentration of Oxygen (O\u2082) outside the cell and zero inside. What happens to the molecules over time?",
      answer:
        'Students should see O\u2082 molecules moving into the cell until the concentrations are roughly equal. They should notice that molecules continue to move back and forth even after the "bar charts" show the levels are balanced (dynamic equilibrium).',
    },
    {
      question:
        'Selective Permeability: Try to add Glucose to the "Outside" and observe its interaction with the plain membrane. Can it pass through on its own?',
      answer:
        "Students should observe that Glucose molecules bounce off the membrane and cannot cross without help. This helps them realize that larger or polar molecules require specific transport proteins to enter or exit.",
    },
    {
      question:
        'Facilitated Diffusion (Channels): In the "Facilitated Diffusion" screen, add several "Leakage Channels" to the membrane. How does this change the movement of ions?',
      answer:
        'Students should see ions start moving through the channels from the side with higher concentration to lower concentration. They should notice that the protein doesn\'t "push" them; it simply provides a path for diffusion.',
    },
    {
      question:
        'Voltage-Gated Channels: Add one or more "Voltage-Gated Channels". Adjust the "Membrane Potential" slider. What happens to the specialized voltage-gated channels when the electrical charge changes?',
      answer:
        'Students should notice the channels "snap" open or closed depending on the voltage setting. They should conclude that some transport proteins are regulated by electrical signals rather than just concentration.',
    },
    {
      question:
        'Ligand-Gated Channels: Drag a "Ligand" molecule to its matching protein channel. What is required for this specific door to open?',
      answer:
        'Students should see that the channel remains closed until the ligand binds to it, causing a "conformational change" (shape change) to open the path.',
    },
    {
      question:
        'Active Transport (Pumps): Switch to the "Active Transport" screen and use the Na\u207A/K\u207A pump. Make sure you have Na\u207A, K\u207A and ATP selected. In which direction are the ions moved compared to their concentration gradient?',
      answer:
        'Students should see ions being pushed toward the side that already has a higher concentration. They should observe that this process "costs" ATP, representing energy used to move substances "uphill".',
    },
    {
      question:
        "Secondary Active Transport: Set up the Na\u207A/Glucose cotransporter. How does the movement of Sodium (Na\u207A) affect the movement of Glucose?",
      answer:
        'Students should observe that Glucose "hitches a ride" with Sodium as it moves into the cell. They should notice that if the Sodium gradient is lost, the transport of Glucose also stops.',
    },
    {
      question:
        'Concentration & Rate: Compare a membrane with one channel to a membrane with five channels. How does the number of "doors" affect the time it takes to reach equilibrium?',
      answer:
        "Students should notice that more channels allow the concentrations to level out much faster. They should conclude that the rate of facilitated diffusion is limited by the number of available transport proteins.",
    },
    {
      question:
        "Dynamic Equilibrium: Once the concentrations on both sides look equal, watch the bar charts for a minute. Do the molecules stop moving entirely?",
      answer:
        'Students should see that individual molecules still cross the membrane, but the net flux is zero (the bars stay level). They should learn that "equilibrium" is a state of constant motion, not a total stop.',
    },
    {
      question:
        "Identifying Mechanisms: If you see a molecule moving from low concentration to high concentration, what must be present in the simulation for that to happen?",
      answer:
        "Students should identify that an active transporter (pump) and ATP (energy) must be involved. They should notice that without ATP, the pump remains inactive.",
    },
  ]),

  // Mean: Share and Balance
  cmgl7sdlq0007l990xcq1o2hw: JSON.stringify([
    {
      question:
        'On the first screen, adjust the water levels so they are all different. Click the "Level" button. What happens to the water?',
      answer:
        'Water moves between containers until every cup has the exact same level. The total amount of water stays the same, but it is "shared" equally.',
    },
    {
      question:
        'Before clicking "Level," turn on "Predict Mean" and use the pencil to guess where the mean will be. How do you decide where to place it?',
      answer:
        'Look for a middle point\u2014higher than the lowest cup but lower than the highest\u2014where "extra" water from tall cups fills "gaps" in short ones.',
    },
    {
      question:
        "Create a screen with 4+ people. How can you give them all a fair number of candy bars?",
      answer:
        "Drag and drop candy bars until they are relatively even. If the mean is not a whole number, they may notice it cannot be perfectly even.",
    },
    {
      question:
        '(Context: Adding a person with no items). What happens to the mean when a "zero" is added?',
      answer:
        'The person with "zero" still counts as a divisor. The mean drops because the same total items are now shared among more people.',
    },
    {
      question:
        "Place two soccer balls on '2' and two on '8'. Where must the fulcrum (triangle) be to keep it level?",
      answer:
        "The balance point is exactly at '5'. This is the middle point because the distances to the balls on the left and right are equal.",
    },
    {
      question:
        "Place one ball on '0' and two balls on '6'. Does the balance point stay in the middle, or move toward the two balls?",
      answer:
        "The balance point moves toward the '6'. The \"weight\" of the data points pulls the mean toward the higher concentration of values.",
    },
    {
      question:
        "Place five balls on '5' and one ball on '10'. How does that single ball on the far right affect the balance point?",
      answer:
        'The mean shifts to the right, away from the group of five. One extreme value (an outlier) can change the "average" for the whole group.',
    },
    {
      question:
        "Build a data set where the mean is exactly '7', but no data points are on '7'. How can you achieve this?",
      answer:
        "Experiment with placing balls symmetrically (e.g., at 6 and 8). The mean doesn't have to be a value that actually exists in the data set.",
    },
    {
      question:
        "If you move all the soccer balls to the same spot, what happens to the mean?",
      answer:
        "The mean moves to that exact spot. If there is no variation in the data, the mean is simply the value of the items themselves.",
    },
  ]),

  // States of Matter: Basics
  cmgl7sdlq0026l990rwl9u95j: JSON.stringify([
    {
      question:
        'Identifying Solids: Start in the "States" experience. Select "Neon" and ensure it is in the Solid state. How would you describe the motion and arrangement of the atoms?',
      answer:
        "Students should notice that the atoms are packed tightly together in a neat, organized pattern. They should see that the atoms aren't still; they are vibrating in place but not moving past one another.",
    },
    {
      question:
        "Heating Up: Use the bucket at the bottom to add Heat. What is the first change you notice in the behavior of the atoms as they transition to a Liquid? (You can click 'liquid' or 'gas' to see what each looks like).",
      answer:
        'Students should observe that the atoms begin to move faster and "break away" from their fixed positions. They should notice that while the atoms are still close together, they are now sliding and rolling over each other.',
    },
    {
      question:
        "The Gas Phase: Continue heating the substance until it becomes a Gas. How does the distance between atoms in a gas compare to a solid or liquid?",
      answer:
        'Students should see that the atoms now fly around independently and occupy the entire container. They should notice there is a vast amount of "empty space" between atoms compared to the other states.',
    },
    {
      question:
        'Cooling Down: Switch the bucket to Cool (ice). What happens to the "Kinetic Energy" (speed) of the atoms, and how does this affect the shape of the substance?',
      answer:
        'Students should observe the atoms slowing down significantly. As they lose energy, they "clump" back together at the bottom of the container, taking on a more defined shape as they return to a liquid or solid state.',
    },
    {
      question:
        "Comparing Substances: Compare Neon and Water (H\u2082O) in their Solid states. What do you notice about how the water molecules are arranged compared to the neon atoms?",
      answer:
        "Students should notice that water molecules form a very specific, open hexagonal-like lattice structure with gaps, whereas neon is more tightly packed. This helps explain why ice is less dense than liquid water.",
    },
    {
      question:
        'Phase Changes: Watch the thermometer while you heat a solid. Does the substance turn into a gas all at once, or is there a "messy" middle stage?',
      answer:
        "Students should notice a transition period where some molecules gain enough energy to break free (evaporate/sublimate) while others stay clumped. They should see that state changes are a gradual process of overcoming attractive forces.",
    },
    {
      question:
        "Changing Units: Toggle the temperature from Kelvin to Celsius. At what temperature (Celsius) do the Oxygen molecules seem to stop moving almost entirely?",
      answer:
        "Students should see that at 0 K (Absolute Zero) or \u2212273\u00B0C, motion stops. This helps them understand that temperature is a direct measurement of the average kinetic energy (motion) of the particles.",
    },
    {
      question:
        "Attraction vs. Motion: If you stop adding heat, why don't the gas atoms just stay stuck to the top of the container? Why do they eventually settle if cooled?",
      answer:
        'Students should realize that there are attractive forces (Intermolecular Forces) trying to pull atoms together. They should notice that as they cool, their motion is no longer strong enough to overcome that "stickiness."',
    },
    {
      question:
        'Volume and Shape: On the Phase Changes screen, use the finger to push down the lid of the container. What happens to the temperature and the frequency of "collisions" against the walls?',
      answer:
        "Students should see the temperature rise and the atoms hit the walls more often. This introduces the relationship between volume, pressure, and temperature (Gas Laws).",
    },
    {
      question:
        "Molecular Composition: Look closely at the Water (H\u2082O) molecules. When the water boils and turns into a gas, do the molecules stay together, or do they break apart into separate Hydrogen and Oxygen atoms?",
      answer:
        'Students should observe that the H\u2082O molecules stay intact as "Mickey Mouse" shaped units. This is a crucial distinction between a physical change (phase change) and a chemical change (breaking bonds).',
    },
  ]),

  // Gravity and Orbits
  cmgl7sdlq0028l990n888yr3w: JSON.stringify([
    {
      question:
        'Establishing Orbit: On the "Model" screen, press the Play button. What keeps the Earth moving in a circle around the Sun instead of flying off into space? Try turning on different guides to help you figure it out.',
      answer:
        "Students should observe that the blue gravity force arrow is constantly pulling the Earth toward the Sun. They should realize that without this pull, the Earth would move in a straight line away from the Sun.",
    },
    {
      question:
        "Gravity and Mass: Increase the mass of the Sun using the slider. How does this change the blue force arrows and the path of the Earth?",
      answer:
        "Students should notice the force arrows grow much larger. They should see that a more massive Sun pulls harder, eventually causing the Earth to spiral inward and crash if the gravity becomes too strong.",
    },
    {
      question:
        "The Role of Distance: Move the Earth further away from the Sun and press play. How does the strength of the gravity arrows change as the distance increases?",
      answer:
        "Students should see the gravity arrows shrink significantly. They should conclude that the further apart two objects are, the weaker the gravitational pull between them becomes.",
    },
    {
      question:
        'Velocity and Inertia: Click the "Velocity" box to show the green arrow. What happens to the Earth if you turn "Gravity" off while it is orbiting?',
      answer:
        'Students should see the Earth travel in a straight line in the direction the green arrow was pointing. They should realize that "inertia" wants the planet to move straight, while gravity keeps it in a curve.',
    },
    {
      question:
        'Satellite Motion: Switch to the "Earth and Satellite" model. How does the speed of a satellite orbiting Earth compare to the speed of the Moon?',
      answer:
        "Students should notice the satellite moves much faster than the Moon. This helps them understand that objects closer to a planet need higher speeds to maintain their orbit and avoid falling into the atmosphere.",
    },
    {
      question:
        "Crashing and Escaping: Try to find a way to make the Moon crash into the Earth. What changes did you have to make to the Moon's mass or speed?",
      answer:
        'Students will likely find that decreasing the Moon\'s speed (velocity) or moving it much closer to Earth causes a crash. This demonstrates that an orbit is a balance of "falling" and "moving sideways" fast enough to miss.',
    },
    {
      question:
        'Path Trajectories: Turn on the "Path" feature. Compare a circular orbit to one where you have slightly increased the planet\'s speed. What shape does the path become?',
      answer:
        "Students should observe the path changing from a circle to an ellipse (oval). This introduces Kepler's laws and the idea that most orbits in space are not perfect circles.",
    },
    {
      question:
        "Mutual Attraction: Look at the gravity arrows on both the Sun and the Earth. Which arrow is longer, and what does this tell you about the pull between them?",
      answer:
        'Students should notice the arrows are the exact same length. This is a great "aha!" moment for Newton\'s Third Law: even though the Sun is huge, it pulls on Earth with the same amount of force that Earth pulls on the Sun.',
    },
    {
      question:
        "Mass vs. Weight: Switch to the \"To Scale\" screen. If you increase the Earth's mass but keep the Moon the same, does the Moon's speed need to change to stay in orbit?",
      answer:
        "Students should see that as the Earth becomes more massive, the Moon must move faster to stay in orbit. If the speed stays the same, the increased gravity will pull the Moon into a crash.",
    },
    {
      question:
        "Predicting Orbits: Based on what you've seen, if a star suddenly lost half of its mass, what would happen to the planets orbiting it?",
      answer:
        "Students should predict that the gravitational pull would weaken (smaller arrows), causing the planets to fly further away or potentially escape the star's orbit entirely.",
    },
  ]),
};

/**
 * Returns mock discussion questions (JSON string) for a whitelisted PhET
 * simulation, or null if none are defined for the given content ID.
 */
export function getPhetMockDiscussionQuestions(
  contentId: string,
): string | null {
  return PHET_DISCUSSION_QUESTIONS_BY_CONTENT_ID[contentId] ?? null;
}
